import { Link } from 'react-router-dom';
import { Instagram, Facebook, Phone, Mail, MapPin, Clock } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_06699631-f431-4d95-91e9-84b8a38f9dc9/artifacts/3e9c2w6a_my-project-page-1%20%285%29.png";

// TikTok icon component
const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const Footer = () => {
  return (
    <footer data-testid="main-footer" className="bg-[#0A0A0A] border-t border-white/10">
      {/* Main Footer */}
      <div className="container-custom py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/">
              <img 
                src={LOGO_URL} 
                alt="UNATHI by Onyx Hairport" 
                className="h-16 mb-6"
              />
            </Link>
            <p className="text-[#999] text-sm leading-relaxed mb-6">
              A premium unisex salon dedicated to delivering excellent haircare, 
              beauty, grooming, and styling services for Black and African hair.
            </p>
            {/* Social Icons */}
            <div className="flex gap-3">
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="social-icon"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="social-icon"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="https://tiktok.com" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-tiktok"
                className="social-icon"
                aria-label="TikTok"
              >
                <TikTokIcon size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-xl text-[#F5F5F5] mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Our Services', path: '/services' },
                { name: 'Style Gallery', path: '/gallery' },
                { name: 'Book Appointment', path: '/book' },
                { name: 'Contact Us', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-[#999] text-sm hover:text-[#C9A227] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-xl text-[#F5F5F5] mb-6">Our Services</h4>
            <ul className="space-y-3">
              {[
                'Braids & Cornrows',
                'Wig Installation',
                'Natural Hair Styling',
                'Silk Press',
                'Men\'s Haircuts',
                'Beard Grooming',
                'Locs & Twists',
                'Bridal Styling',
              ].map((service) => (
                <li key={service}>
                  <span className="text-[#999] text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-xl text-[#F5F5F5] mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#C9A227] mt-1 flex-shrink-0" />
                <span className="text-[#999] text-sm">
                  123 High Street<br />
                  London, UK
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#C9A227] flex-shrink-0" />
                <a 
                  href="tel:+447000000000" 
                  className="text-[#999] text-sm hover:text-[#C9A227] transition-colors"
                >
                  +44 7000 000000
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#C9A227] flex-shrink-0" />
                <a 
                  href="mailto:hello@unathi.co.uk" 
                  className="text-[#999] text-sm hover:text-[#C9A227] transition-colors"
                >
                  hello@unathi.co.uk
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-[#C9A227] mt-1 flex-shrink-0" />
                <div className="text-[#999] text-sm">
                  <p>Mon - Fri: 9am - 7pm</p>
                  <p>Sat: 9am - 6pm</p>
                  <p>Sun: 10am - 4pm</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#666] text-xs text-center md:text-left">
            © {new Date().getFullYear()} Onyx Hairport trading as UNATHI. All Rights Reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-[#666] text-xs hover:text-[#C9A227] transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-[#666] text-xs hover:text-[#C9A227] transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
